import {
  AfterViewInit, ChangeDetectorRef, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, inject,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { NumericFieldPolicy, resolveNumericPolicy } from '../utils/numeric-policy.util';
import { sanitizeToPolicy } from '../utils/numeric-strategies.util';
import { SchemaControlLike } from '../utils/schema-min.util';

/**
 * Applies a schema control's resolved {@link NumericFieldPolicy} to its input.
 *
 * ## Why this exists
 *
 * Numeric presentation used to be declared per template, by hand, at 122
 * `<p-inputnumber>` sites across 20 sheet templates — and it drifted into four
 * different spellings of one rule (`[minFractionDigits]` alone appeared as
 * `"2"`, `"0"`, a `control.type` ternary, and a `ui.decimalPlaces` read), while
 * `[min]` reached only 44 of those sites. This directive makes the form JSON
 * the single declaration point and this class the only executor.
 *
 * Bounds are NOT re-derived here — `resolveNumericPolicy` reuses
 * `getSchemaMin`/`getSchemaMax`, so the mounted and headless paths cannot drift
 * on negativity rules.
 *
 * ## Why `min`/`max` are pushed onto the PrimeNG instance directly, not via `[min]`/`[max]`
 *
 * A caller might expect to template-bind `[min]="policy.min"` alongside the
 * `[minFractionDigits]`/`[useGrouping]` bindings. Do not — PrimeNG's `InputNumber.min`
 * and `.max` `@Input()`s are declared with Angular's `numberAttribute` transform, whose
 * fallback for a non-numeric bound value (i.e. our intentional `undefined` for an
 * unconstrained field) is `NaN`, not `undefined`. A bound `[min]="undefined"` therefore
 * does not leave the field unset — every CD cycle coerces it to `NaN`, which (a) still
 * renders `aria-valuemin="NaN"` instead of omitting the attribute (`[attr.aria-valuemin]`
 * only omits on `null`/`undefined`, and `NaN` is neither), and (b) breaks
 * `InputNumber.allowMinusSign()` (`this.min == null || this.min < 0`), silently blocking
 * the `-` key on unconstrained (signed, e.g. monetary) fields. `minFractionDigits`/
 * `maxFractionDigits` do not have this problem — PrimeNG declares their transform with an
 * explicit `null` fallback, not `NaN`.
 *
 * Setting `.min`/`.max` here via plain property assignment (not a template binding) never
 * invokes that transform, so `undefined` stays `undefined`. Because `InputNumber` is
 * `OnPush`, the assignment is followed by `markForCheck()` on ITS OWN `ChangeDetectorRef` —
 * injecting `ChangeDetectorRef` from a directive co-located on a component's host element
 * returns that COMPONENT's own view (Angular's `injectChangeDetectorRef` special-cases
 * `isComponentHost(tNode)` for exactly this), not this directive's enclosing component's
 * view, regardless of the `self` flag (which is inert for this token — the resolution
 * this depends on is the host-node/`isComponentHost` special case, not `self`). Without
 * this, the corrected value would compute but the component's OnPush view would never
 * repaint to show it.
 *
 * ## Phase 2 — USER INPUT ONLY (2026-08-17)
 *
 * This directive sanitises what the user types and pastes. It does **not**
 * touch values arriving from API hydration, calculations or programmatic
 * `setValue`. An LLM-extracted `1000.9` on an integer field, or a 25-digit
 * figure on a 15-digit field, reaches the control unchanged.
 *
 * ### Why the hydration pass was removed
 *
 * Phase 1 rewrote hydrated values as temporary compensation for the backend not
 * sanitising LLM-extracted values at source. It could only ever half-work, and
 * the half that failed was invisible: the correction was written to the
 * `FormControl` and nowhere else, while the sheet's baseline and the persisted
 * modified state — which is what the submission payload is built from — kept
 * the original value. So a coerced field
 *
 *   - DISPLAYED the capped number,
 *   - was badged `Edited` on load with no user action, because
 *     `computeRowStatus` compares the form against
 *     `FilingFormStateService.getOriginalSheetData`, and
 *   - SUBMITTED the original, over-length number regardless.
 *
 * A P.O. defect on 2026-08-17 surfaced all three at once on Notes – Issued
 * Capital (`ssmt-mfrs:OtherChangesInNumberOfSharesIssuedAndFullyPaid` returned
 * at 25 digits). Capping the display while submitting something else is
 * strictly worse than showing the value that will actually be sent, so the
 * hydration arm was removed rather than extended into the state layer.
 *
 * Trimming extracted values now belongs to the backend, at extraction, which is
 * the only layer that can fix a value nobody opens. The rule it must match is
 * `capIntegerDigits` — hand-off:
 * `docs/shared-services/numeric-digit-cap-llm-extraction-backend-handoff-20260817.md`.
 *
 * ### What this means for the code
 *
 * There is deliberately **no `valueChanges` subscription, no `MutationObserver`
 * and no deferred initial pass** — those three were the hydration arm, and
 * re-adding any of them reintroduces the divergence. The keystroke, `input` and
 * `paste` handlers are the whole surface. Cover:
 * `schema-numeric.directive.spec.ts`, the "Phase 2" specs — a hydrated
 * over-length value must survive untouched while a pasted one is still capped.
 *
 * A corrected value is also written back to the input element directly — see
 * `resyncDisplay` for why `setValue` alone leaves the display stale.
 *
 * ## Pencil-flag ordering
 *
 * No longer a concern on the hydration path: this directive does not write
 * during hydration at all, so it cannot raise a sheet's pencil or badge a row
 * `Edited` on a page load with no user action. User-driven writes still use
 * `emitEvent: false` so they cannot re-enter the pipeline that invoked them;
 * the sheet's own edit pipeline owns the flag from there.
 *
 * ## Paste of an over-length value — first digits win (2026-08-17, PO ruling)
 *
 * `onPaste` hands the clipboard text straight to `writeSanitized`, so the
 * surviving digits are whatever `sanitizeToPolicy` -> `capIntegerDigits` keeps:
 * the FIRST `maxIntegerDigits` digits read left-to-right, then the next
 * `decimals` digits as the fraction, the rest discarded. Pasting
 * `1234567890123456789` into a 13+2 monetary field yields `1234567890123.45`.
 *
 * This reversed the original rule (keep the LAST digits, retain the pasted
 * fraction). The reversal is in the pure helper, not here — the same direction
 * therefore governs API hydration and programmatic writes, by design. Read
 * `capIntegerDigits`' docblock before changing it; it records the accepted cost
 * (a pasted fraction is discarded in favour of positional digits) and the
 * leading-zero guard that left-anchoring made necessary.
 *
 * ## Overtype at the cap (2026-08-11, PO defect)
 *
 * `capIntegerDigits` (see `resyncDisplay`'s docblock and `numeric-strategies.util.ts`)
 * corrects an over-cap value by truncating it. That rule is right for a paste or a
 * hydrated value, but it makes a mid-string keystroke incoherent once the field is
 * already AT its integer-digit cap: PrimeNG's default behaviour is to INSERT the
 * typed digit, which produces one extra integer digit, and the correction then
 * deletes another one to get back to the cap. Whichever end it deletes from, the
 * keystroke does not do what the user asked — the original left-anchored version of
 * this note described the truncation eating the typed digit itself in a run of
 * identical digits; since the 2026-08-17 reversal it instead shifts every digit
 * right of the caret and drops the last one, which is no more comprehensible. Either
 * way the caret has already advanced and the user cannot see what happened.
 *
 * `onKeydownCapture` intercepts a plain digit keystroke, in the CAPTURE phase on
 * `this.el.nativeElement` (ahead of PrimeNG's own `keypress`-based insert), and
 * — ONLY when the integer part is already at `policy.maxIntegerDigits` and the
 * caret sits on a digit within the integer part — replaces that digit in place
 * instead of inserting. The event is fully suppressed (`preventDefault` +
 * `stopPropagation`) so PrimeNG's own handler never runs for that keystroke.
 *
 * This deliberately does NOT apply below the cap: insert stays exactly today's
 * behaviour there, and `capIntegerDigits` still runs its truncation rule for
 * paste and hydration.
 *
 * ## Overtype at the separator (2026-08-14, follow-up PO defect)
 *
 * The overwrite scan runs to the END of the display, not to the decimal
 * separator. On a field with decimals, a caret parked immediately to the LEFT
 * of the "." (e.g. `1,234,567,890,123|.45`) has no integer digit at or after
 * it, so a scan bounded at the separator found nothing and the handler refused
 * the keystroke — after already having suppressed it. The user saw nothing
 * happen, and had to move the caret manually to the right of the "." before
 * the cents would accept a digit. Falling through the separator onto the first
 * cents digit makes that position behave like every other caret position at
 * the cap, and matches PrimeNG's OWN semantics one character further right:
 * inside the fraction region (`insert()`'s `selectionStart > decimalCharIndex`
 * branch) PrimeNG overwrites rather than inserts, which is why this handler
 * hands that region straight back to it. Before the fix the two positions
 * either side of the separator disagreed for no reason a user could see.
 *
 * The refusal branch survives where it is still the only coherent answer: a
 * 0-decimal field with the caret at the end of a full integer, where
 * `sepIndex === display.length` leaves nothing to the right to overwrite.
 *
 * ## Range delete across the decimal separator (2026-09-01, PO defect)
 *
 * The overtype gate above only claims DIGIT keystrokes with a collapsed caret,
 * which left the cap with a second way through: a delete that removes the
 * decimal separator promotes every fraction digit into the integer part in one
 * step, with no digit typed at all. On a 13+2 field
 * `1,111,111,111,111.11` -> select just the "." -> Delete yields
 * `111,111,111,111,111.00` — 15 integer digits, 17 in total — and repeating the
 * gesture grew it 19, 21, 23 without limit. The overflowing value reached the
 * form control, so it was what got persisted and submitted.
 *
 * PrimeNG guards the COLLAPSED-caret case deliberately (`onInputKeyDown`'s
 * Backspace/Delete branches answer `this._decimal.test(deleteChar)` by moving
 * the caret instead of deleting), which is why a caret parked either side of
 * the "." is inert and the defect looked unreproducible. Its RANGE branch has
 * no such guard: `deleteRange` is a blind `slice(0, start) + slice(end)`.
 *
 * The directive's own net cannot catch it either — PrimeNG `preventDefault()`s
 * the key and writes `<input>.value` imperatively in `handleOnInput`, and a
 * programmatic property write dispatches no `input` event, so `sanitizeCurrent`
 * never runs. (Dispatching one by hand DOES repair the value, which is how the
 * mechanism was confirmed; nothing on the keyboard path ever does.)
 *
 * `handleSeparatorRangeDelete` therefore claims `Backspace`/`Delete` when the
 * selection spans the separator on a `decimals > 0` field AND the merged digits
 * would overflow — below the cap PrimeNG's own merge is correct and is left
 * alone. Structurally impossible on a 0-decimal field: there is no separator to
 * remove, which is exactly why the PO saw this only on fields with cents.
 *
 * ### Why an overflowing merge is REFUSED, not re-seated (2026-09-01, follow-up)
 *
 * The first cut of this gate ran the surviving digits through
 * `sanitizeToPolicy`, reusing the left-anchored truncation that governs paste
 * and hydration. That is wrong for this gesture. On a field UNDER the cap the
 * promoted cents push the stream one digit past `maxIntegerDigits`, so the
 * point is re-seated one place to the right and the value is silently
 * MULTIPLIED: `123,456,789,012.00` -> select "." -> Backspace produced
 * `1,234,567,890,120.00`, which reads as an invented `0` (P.O., 2026-09-01).
 * Changing a preparer's figure by an order of magnitude is the exact harm the
 * left-anchored rule was adopted to prevent — but truncation only avoids it
 * when the value ARRIVES over-length, which is not this case.
 *
 * So the gate now simply suppresses the keystroke: if the separator cannot be
 * removed without breaking the cap, it is not removed. Nothing is written, so
 * the control keeps its value and stays clean. That is also the answer the two
 * neighbouring precedents already give — PrimeNG refuses a collapsed-caret
 * delete of the separator, and the overtype branch refuses rather than corrupt
 * when it has nothing to overwrite — and it makes the at-cap case explicit
 * rather than relying on truncation happening to return the same number.
 *
 * Usage:
 * ```html
 * <p-inputnumber appSchemaNumeric [schemaControl]="control" [formControlName]="control.qname">
 * </p-inputnumber>
 * ```
 */
@Directive({
  selector: '[appSchemaNumeric]',
  standalone: true,
})
export class SchemaNumericDirective implements OnInit, AfterViewInit, OnDestroy {
  private el = inject(ElementRef);
  private ngControl = inject(NgControl, { optional: true });
  /** `{ self: true }` resolves `InputNumber` to the co-located `p-inputnumber`
   *  instance on this same host element, not an ancestor. `ChangeDetectorRef`
   *  resolves to that component's OWN view regardless of `self` (Angular's
   *  `isComponentHost(tNode)` special-case, not the `self` flag — see the
   *  "min/max pushed directly" section of the class docblock); `self: true`
   *  is kept here only so a future host-element change can't accidentally
   *  widen the lookup. */
  private inputNumberRef = inject(InputNumber, { optional: true, self: true });
  private inputNumberCdr = inject(ChangeDetectorRef, { optional: true, self: true });

  @Input({ required: true }) schemaControl!: SchemaControlLike;

  /** Resolved policy — public so specs and templates can assert against it. */
  policy: NumericFieldPolicy | null = null;

  private isPrimeNgInputNumber = false;
  private inputElement: HTMLInputElement | null = null;
  private applying = false;

  ngOnInit(): void {
    this.policy = resolveNumericPolicy(this.schemaControl);
    this.isPrimeNgInputNumber = this.el.nativeElement.tagName.toLowerCase() === 'p-inputnumber';

    // Must happen here, not in ngAfterViewInit: co-located directives/components
    // on one element all run ngOnInit before Angular refreshes that component's
    // OWN view for the first time, so an ngOnInit assignment is visible on
    // `p-inputnumber`'s very first render — no extra change-detection pass (and
    // so no markForCheck()) is needed for the INITIAL paint. See the class
    // docblock section on why this bypasses `[min]`/`[max]` template binding.
    if (this.isPrimeNgInputNumber && this.inputNumberRef) {
      this.inputNumberRef.min = this.policy.min;
      this.inputNumberRef.max = this.policy.max;
    }
  }

  ngAfterViewInit(): void {
    this.inputElement = this.isPrimeNgInputNumber
      ? this.el.nativeElement.querySelector('input')
      : this.el.nativeElement;

    if (this.isPrimeNgInputNumber && this.inputNumberRef && this.policy) {
      // Defensive re-assertion for any LATER re-render (e.g. an ancestor
      // OnPush component's own markForCheck sweeping through) that might
      // otherwise re-evaluate stale bound state. OnPush needs an explicit
      // markForCheck() here since this hook runs after the view's own first
      // paint, unlike the ngOnInit assignment above.
      this.inputNumberRef.min = this.policy.min;
      this.inputNumberRef.max = this.policy.max;
      this.inputNumberCdr?.markForCheck();
    }

    if (this.isPrimeNgInputNumber && this.inputElement) {
      // USER-INPUT paths only. There is deliberately no MutationObserver and no
      // `valueChanges` subscription here — see the class docblock, "Phase 2".
      this.inputElement.addEventListener('input', () => this.sanitizeCurrent());
      this.inputElement.addEventListener('paste', (pasteEvent) => this.onPaste(pasteEvent as ClipboardEvent));
    }

    if (this.isPrimeNgInputNumber) {
      // Capture phase: must run BEFORE PrimeNG's own `keydown`/`keypress`
      // listeners on the inner `<input>`, which are attached in the bubble
      // phase during ITS `ngAfterViewInit`. Registering here on the host
      // element, in capture, guarantees this handler sees the event first
      // regardless of PrimeNG's own listener registration order — see the
      // class docblock section "Overtype at the cap".
      this.el.nativeElement.addEventListener(
        'keydown',
        this.onKeydownCapture,
        { capture: true },
      );
    }
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener(
      'keydown',
      this.onKeydownCapture,
      { capture: true },
    );
  }

  @HostListener('paste', ['$event'])
  onPasteEvent(event: ClipboardEvent): void {
    if (!this.isPrimeNgInputNumber)
      this.onPaste(event);
  }

  private onPaste(event: ClipboardEvent): void {
    if (!this.policy)
      return;
    const pasted = event.clipboardData?.getData('text') ?? '';
    event.preventDefault();
    this.writeSanitized(pasted);
  }

  /**
   * Overtype-at-cap keystroke gate. See the class docblock, "Overtype at the
   * cap", for why this exists. An arrow-function class field (not a method)
   * so `addEventListener`/`removeEventListener` in `ngAfterViewInit`/
   * `ngOnDestroy` refer to the SAME bound function reference — a `.bind(this)`
   * on each call would register/deregister two different functions and leak
   * the listener.
   */
  private onKeydownCapture = (event: KeyboardEvent): void => {
    if (!this.policy || !this.isPrimeNgInputNumber || !this.inputElement)
      return;
    // Registered on the host element (see `ngAfterViewInit`), so a keydown
    // bubbling up from some OTHER focusable descendant would otherwise be
    // handled against this input's stale selection. Inert today — neither
    // wired sheet enables PrimeNG's spinner buttons — but cheap to guard.
    if (event.target !== this.inputElement)
      return;
    if (event.ctrlKey || event.metaKey || event.altKey)
      return;

    const { selectionStart, selectionEnd, value: display } = this.inputElement;
    if (selectionStart === null || selectionEnd === null)
      return;

    // A range delete is the OTHER way a keystroke can grow the integer part
    // past the cap — see "Range delete across the decimal separator" in the
    // class docblock. Claimed before the digit test because the keys differ.
    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.handleSeparatorRangeDelete(event, display, selectionStart, selectionEnd);
      return;
    }

    if (!/^[0-9]$/.test(event.key))
      return;
    if (selectionStart !== selectionEnd)
      return;

    const { decimals, maxIntegerDigits } = this.policy;
    const sepIndex = decimals > 0 ? display.length - decimals - 1 : display.length;
    const intRegion = display.slice(0, sepIndex);
    const intDigits = intRegion.replace(/\D/g, '').length;

    if (intDigits < maxIntegerDigits)
      return; // Below the cap: leave PrimeNG's insert behaviour untouched.
    if (selectionStart > sepIndex)
      return; // Caret is already inside the fraction part — PrimeNG overwrites there itself.

    // This keystroke is now committed to overtype-at-cap handling one way or
    // another — either a replace below, or a silent refusal. Either way
    // PrimeNG's own insert must not also run.
    event.preventDefault();
    event.stopPropagation();

    // The scan runs to the END of the display, not to `sepIndex`, so a caret
    // parked immediately LEFT of the decimal point falls through the separator
    // onto the first cents digit — see "Overtype at the separator" in the class
    // docblock. On a 0-decimal field `sepIndex === display.length`, so this is
    // the same bound it always was and the refusal branch below still applies.
    let replacedIndex = -1;
    for (let charIndex = selectionStart; charIndex < display.length; charIndex++) {
      if (/\d/.test(display[charIndex])) {
        replacedIndex = charIndex;
        break;
      }
    }
    if (replacedIndex === -1)
      return; // Nothing to overwrite — refuse rather than corrupt.

    const next = display.slice(0, replacedIndex) + event.key + display.slice(replacedIndex + 1);

    const sign = display.startsWith('-') ? '-' : '';
    const intPart = next.slice(0, sepIndex).replace(/\D/g, '');
    const fracPart = next.slice(sepIndex + 1).replace(/\D/g, '');
    const numeric = decimals > 0 ? `${sign}${intPart}.${fracPart}` : `${sign}${intPart}`;
    const value = Number(numeric);

    // Overtyping the LEFT-MOST integer digit with `0` can collapse leading
    // zeros out of `numeric` (e.g. "0,111,111,111,111.11" -> 111111111111.11,
    // 12 digits, not 13). `next` is then a hand-built string one character
    // longer than what the stored NUMBER actually formats to — writing `next`
    // to the DOM would show a digit count the control does not hold, the same
    // silent-truncation symptom this handler exists to prevent. Reconciling
    // against PrimeNG's OWN `formatValue` keeps the two in lockstep; when
    // nothing collapses, `formatted === next` and the caret math below is a
    // no-op (delta 0).
    const formatted = this.inputNumberRef ? this.inputNumberRef.formatValue(value) : next;
    const caretTarget = Math.max(0, replacedIndex + 1 - (next.length - formatted.length));

    this.applying = true;
    try {
      this.ngControl?.control?.setValue(value);
      // A PrimeNG keystroke reaches the form control's `dirty` flag through
      // the CVA's `onChange`; this is a direct `setValue`, which does not.
      // Sheets (e.g. role23) gate error messages on `dirty || touched`, so
      // without this an overtype past a schema `max` shows no message until
      // blur.
      this.ngControl?.control?.markAsDirty();
      this.inputElement.value = formatted;
      this.inputElement.setSelectionRange(caretTarget, caretTarget);
    } finally {
      this.applying = false;
    }
  };

  /**
   * Range-delete gate for a selection that swallows the decimal separator.
   * See the class docblock, "Range delete across the decimal separator", for
   * why PrimeNG's own separator protection does not reach this case.
   *
   * Declared as a method (not a class field) because it is only ever called
   * from `onKeydownCapture`, which already owns the listener identity.
   */
  private handleSeparatorRangeDelete(
    event: KeyboardEvent,
    display: string,
    selectionStart: number,
    selectionEnd: number,
  ): void {
    if (!this.policy || !this.inputElement)
      return;

    const { decimals, maxIntegerDigits } = this.policy;
    if (decimals <= 0)
      return; // No separator exists, so no fraction digit can be promoted.
    if (selectionStart === selectionEnd)
      return; // Collapsed caret — PrimeNG refuses to delete the separator itself.
    if (event.shiftKey)
      return; // PrimeNG treats a shifted key as a special char and deletes nothing.

    // Same positional convention as the overtype gate above: the display is
    // always PrimeNG-formatted when a keystroke arrives, and `minFractionDigits`
    // equals `decimals` at every call site, so the separator sits exactly
    // `decimals + 1` characters from the end.
    const sepIndex = display.length - decimals - 1;
    if (sepIndex < selectionStart || sepIndex >= selectionEnd)
      return; // Separator survives — a plain delete can only shorten the value.

    // With the separator gone every surviving digit becomes an integer digit.
    // Below the cap that is exactly what the user asked for and PrimeNG's own
    // handling is correct, so only an OVERFLOWING merge is intercepted; this
    // keeps the blast radius to the case that was broken.
    const surviving = (display.slice(0, selectionStart) + display.slice(selectionEnd)).replace(/\D/g, '');
    if (surviving.replace(/^0+(?=\d)/, '').length <= maxIntegerDigits)
      return;

    // Refuse, do not re-seat — see "Why an overflowing merge is refused" in the
    // class docblock. Suppressing the event is the whole correction: the value,
    // the display and the selection are all left exactly as they were.
    event.preventDefault();
    event.stopPropagation();
  }

  private sanitizeCurrent(): void {
    const raw = this.ngControl?.control?.value;
    if (raw === null || raw === undefined || raw === '')
      return;
    this.writeSanitized(String(raw));
  }

  /** Sanitise and write back, guarded against re-entry. */
  private writeSanitized(raw: string): void {
    if (!this.policy || this.applying || !this.ngControl?.control)
      return;

    const sanitized = sanitizeToPolicy(raw, this.policy);
    const next = sanitized === '' || sanitized === '-' ? null : Number(sanitized);

    if (next !== null && Number.isNaN(next))
      return;
    if (this.ngControl.control.value === next)
      return;

    this.applying = true;
    try {
      this.ngControl.control.setValue(next, { emitEvent: false });
      this.resyncDisplay(next);
    } finally {
      this.applying = false;
    }
  }

  /**
   * Force the visible input text to match a value this directive just corrected.
   *
   * PrimeNG writes `<input>.value` IMPERATIVELY on every keystroke
   * (`handleOnInput` / `updateInput`), so Angular's `[value]="formattedValue()"`
   * binding slot never learns the DOM changed. A `setValue` from here reaches
   * only `InputNumber.writeValue`, which assigns `this.value` and calls
   * `markForCheck()` — it does not touch the DOM. So when a correction returns
   * the control to a value whose formatted form equals what Angular last wrote,
   * `bindingUpdated`'s `Object.is` check passes, the DOM write is SKIPPED, and
   * PrimeNG's stale text survives. Symptom: the box shows an over-length number
   * while the control — and the submission — holds the cap.
   *
   * Formatting is delegated to PrimeNG's own `formatValue` so locale, grouping
   * and fraction digits cannot drift from what the component itself renders.
   *
   * ## Caret (fixed 2026-08-11, PO defect)
   *
   * Two things used to be true here and both were wrong:
   *
   * 1. This method wrote unconditionally whenever `writeSanitized` reached it.
   *    `writeSanitized` only calls in after its OWN equality guard
   *    (`control.value === next`) has already confirmed the sanitised NUMBER
   *    changed — so by the time we get here a numeric correction is always
   *    genuine. But a changed NUMBER does not guarantee changed TEXT: nothing
   *    upstream ever compared `next`'s formatted form against what the input
   *    already displays. The guard costs nothing and removes a pointless DOM
   *    write.
   * 2. Every write assigned `.value` outright, which moves the caret to the
   *    end. The former docblock here claimed that was fine because "the end
   *    is where the caret belongs anyway" for a correction — that reasoning
   *    doesn't survive contact with a fixed-decimals field: `minFractionDigits`
   *    pins a permanent ".00" suffix, so the caret's natural position while
   *    editing the integer part sits BEFORE that suffix, nowhere near the
   *    string's end. A correction that lands mid-integer-part still snapped
   *    the caret past the decimals. The PO's explicit requirement is NO
   *    repositioning at all, for both the redundant-write case (now a no-op)
   *    and the genuine-correction case (caret now captured and restored) —
   *    the restore is by raw TEXT OFFSET, not by tracking the same digit, so
   *    on a correction that shortens the text the caret ends up one position
   *    to the right of the character it was beside, not glued to that digit.
   *
   * ## Why a write reaches this method at all
   *
   * The native `input` listener never fires during typing: PrimeNG
   * intercepts digits on `keypress`, calls `preventDefault()`, and writes
   * `<input>.value` programmatically, and a programmatic property write does
   * not dispatch an `input` event.
   *
   * A `[maxlength]` binding was tried here and removed — see the "Numeric
   * sheet fields" callout on this directive's `[min]`/`[max]` note in
   * `src/app/components/xbrl-form/CLAUDE.md` for why no single value works
   * for a grouped field. This method's correction path is the per-keystroke
   * net for paste and hydration, and for the one digit-typing case
   * `onKeydownCapture` does not itself claim: below the cap. `onKeydownCapture`
   * (see the class docblock, "Overtype at the cap") now intercepts a plain
   * digit keystroke AT the cap before it ever reaches PrimeNG's insert, so
   * that case no longer lands here at all; `sanitizeToPolicy`'s truncation
   * remains exactly what runs for everything else — paste, hydration, and any
   * non-digit keystroke.
   */
  private resyncDisplay(next: number | null): void {
    if (!this.isPrimeNgInputNumber || !this.inputElement || !this.inputNumberRef)
      return;

    // `formatValue(null)` also returns '' (its `value != null` guard falls
    // through to the empty-string return) — the hard-coded '' below matches
    // PrimeNG's own answer, it isn't a shortcut around it.
    const formatted = next === null ? '' : this.inputNumberRef.formatValue(next);
    if (this.inputElement.value === formatted)
      return;

    const { activeElement } = this.inputElement.ownerDocument;
    const isFocused = activeElement === this.inputElement;
    const { selectionStart, selectionEnd } = this.inputElement;

    this.inputElement.value = formatted;

    // Restoring an offset past the (now shorter) text is safe — browsers clamp
    // `setSelectionRange` to the value's length, so no clamping is done here.
    if (isFocused)
      this.inputElement.setSelectionRange(selectionStart, selectionEnd);
  }
}
